/**
 * Exit Comparison Mock
 *
 * Shows the same 10 trades with three different exit strategies,
 * demonstrating how exits — not entries — define the payoff distribution.
 */

interface ExitStrategy {
  name: string;
  color: string;
  winRate: string;
  avgWin: string;
  avgLoss: string;
  netReturn: string;
  desc: string;
}

const strategies: ExitStrategy[] = [
  {
    name: 'Trailing Stop',
    color: '#22c55e',
    winRate: '40%',
    avgWin: '+5.8%',
    avgLoss: '-1.4%',
    netReturn: '+14.6%',
    desc: 'Lets winners run, cuts losers early',
  },
  {
    name: 'Fixed Target (2:1)',
    color: '#3b82f6',
    winRate: '55%',
    avgWin: '+2.0%',
    avgLoss: '-1.0%',
    netReturn: '+6.5%',
    desc: 'Balanced risk-reward, moderate win rate',
  },
  {
    name: 'Signal Reversal',
    color: '#f59e0b',
    winRate: '35%',
    avgWin: '+7.2%',
    avgLoss: '-2.8%',
    netReturn: '+7.0%',
    desc: 'Stays in trade until opposite signal fires',
  },
];

function MetricCell({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{
        fontSize: '0.5rem', color: '#6b7280', textTransform: 'uppercase',
        letterSpacing: '0.05em', marginBottom: '0.1rem',
      }}>{label}</div>
      <div style={{
        fontSize: '0.8125rem', fontWeight: 600, color: '#d1d5db',
        fontFamily: mono ? 'ui-monospace, monospace' : 'inherit',
      }}>{value}</div>
    </div>
  );
}

export default function ExitComparisonMock() {
  return (
    <div className="platform-mock">
      <div className="platform-mock-header">
        <span className="dot" /> Same Entry, Three Exits
      </div>
      <div className="platform-mock-body" style={{ padding: '1rem' }}>
        <div style={{
          fontSize: '0.625rem', color: '#6b7280', marginBottom: '0.75rem',
          textAlign: 'center', fontStyle: 'italic',
        }}>
          10 identical entry signals &mdash; only the exit rule changes
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {strategies.map((s) => (
            <div key={s.name} style={{
              display: 'grid',
              gridTemplateColumns: '1.4fr repeat(4, 1fr)',
              gap: '0.5rem', alignItems: 'center',
              padding: '0.625rem 0.75rem',
              background: '#181825',
              border: `1px solid ${s.color}25`,
              borderRadius: 6,
            }}>
              <div>
                <div style={{
                  fontSize: '0.75rem', fontWeight: 600, color: s.color,
                  marginBottom: '0.1rem',
                }}>
                  {s.name}
                </div>
                <div style={{ fontSize: '0.5625rem', color: '#6b7280' }}>
                  {s.desc}
                </div>
              </div>
              <MetricCell label="Win Rate" value={s.winRate} />
              <MetricCell label="Avg Win" value={s.avgWin} mono />
              <MetricCell label="Avg Loss" value={s.avgLoss} mono />
              <MetricCell label="Net" value={s.netReturn} mono />
            </div>
          ))}
        </div>

        <div style={{
          marginTop: '0.75rem', padding: '0.5rem 0.75rem',
          background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.15)',
          borderRadius: 6, fontSize: '0.6875rem', color: '#9ca3af',
        }}>
          <span style={{ color: '#22c55e', fontWeight: 600 }}>Takeaway: </span>
          The trailing stop wins the least often but produces the highest return.
          The exit defines the distribution — not the entry.
        </div>
      </div>
      <div className="platform-mock-caption">
        You are not trading signals. You are trading distributions — and distributions are defined by exits.
      </div>
    </div>
  );
}
